import {
  pickFirstValidEmail,
  pickFirstValidPhone,
} from '../../shared/contactValidation';
import {
  type EnrollmentParentInfoInput,
  requiresEnrollmentParentName,
} from './enrollmentSignatureRequirement';

export type EnrollmentIntakeValidationData = {
  family: {
    familyNameEnglish?: unknown;
    address?: unknown;
    city?: unknown;
    state?: unknown;
    zip?: unknown;
    homePhone?: unknown;
  };
  father: EnrollmentParentInfoInput;
  mother: EnrollmentParentInfoInput;
};

export type EnrollmentIntakeValidationResult = {
  valid: boolean;
  step: number;
  message: string;
  fields: string[];
};

const validEnrollmentIntakeStep = (step: number): EnrollmentIntakeValidationResult => ({
  valid: true,
  step,
  message: '',
  fields: [],
});

const invalidEnrollmentIntakeStep = (
  step: number,
  message: string,
  fields: string[],
): EnrollmentIntakeValidationResult => ({
  valid: false,
  step,
  message,
  fields,
});

const readText = (value: unknown): string => String(value ?? '').trim();

const parentLabel = (parent: 'father' | 'mother'): string => (
  parent === 'father' ? "Father's" : "Mother's"
);

const hasFiveDigitZip = (value: unknown): boolean => readText(value).replace(/\D/g, '').length >= 5;

export const validateEnrollmentIntakeStep = (
  data: EnrollmentIntakeValidationData,
  step: number,
  profileEmail = '',
): EnrollmentIntakeValidationResult => {
  if (step === 0) {
    if (!readText(data.family.familyNameEnglish)) {
      return invalidEnrollmentIntakeStep(0, 'Family name is required before continuing.', [
        'family.familyNameEnglish',
      ]);
    }

    const missingAddressFields = [
      ['family.address', data.family.address],
      ['family.city', data.family.city],
      ['family.state', data.family.state],
    ].filter(([, value]) => !readText(value)).map(([field]) => field as string);

    if (!hasFiveDigitZip(data.family.zip)) {
      missingAddressFields.push('family.zip');
    }

    if (missingAddressFields.length) {
      return invalidEnrollmentIntakeStep(
        0,
        'Family address, city, state, and ZIP code are required before continuing.',
        missingAddressFields,
      );
    }
  }

  if (step === 1) {
    const ignoredEmail = readText(profileEmail);
    const completeParentNameExists = Boolean(
      (readText(data.father.firstName) && readText(data.father.lastName))
      || (readText(data.mother.firstName) && readText(data.mother.lastName))
    );

    if (!completeParentNameExists) {
      return invalidEnrollmentIntakeStep(
        1,
        'At least one parent first and last name is required before continuing.',
        ['father.firstName', 'father.lastName', 'mother.firstName', 'mother.lastName'],
      );
    }

    for (const parent of ['father', 'mother'] as const) {
      const parentData = data[parent];
      if (requiresEnrollmentParentName(parentData, { ignoredEmail })) {
        const fields = [
          !readText(parentData.firstName) ? `${parent}.firstName` : null,
          !readText(parentData.lastName) ? `${parent}.lastName` : null,
        ].filter((field): field is string => Boolean(field));
        return invalidEnrollmentIntakeStep(
          1,
          `${parentLabel(parent)} first and last name are required before continuing because ${parent} information was provided.`,
          fields,
        );
      }
    }

    if (!pickFirstValidEmail([data.father.email, data.mother.email, profileEmail])) {
      return invalidEnrollmentIntakeStep(1, 'A valid parent email is required before continuing.', [
        'father.email',
        'mother.email',
      ]);
    }

    if (!pickFirstValidPhone([data.family.homePhone, data.father.cellPhone, data.mother.cellPhone])) {
      return invalidEnrollmentIntakeStep(1, 'Home or parent phone must include a valid phone number before continuing.', [
        'family.homePhone',
        'father.cellPhone',
        'mother.cellPhone',
      ]);
    }
  }

  return validEnrollmentIntakeStep(step);
};
